import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type IconButtonVariant = 'solid' | 'ghost' | 'outline';
export type IconButtonTone = 'default' | 'danger' | 'warning' | 'success';
export type IconButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<IconButtonVariant, string> = {
  solid: 'bg-primary text-on-primary border border-primary hover:bg-primary-hover',
  ghost: 'bg-transparent border border-transparent hover:bg-surface-muted',
  outline: 'bg-transparent border-2 border-line-strong hover:bg-surface-muted',
};

/** Tone wins over variant for the icon colour, so a danger action reads as one. */
const TONES: Record<IconButtonTone, string> = {
  default: '',
  danger: 'text-danger hover:bg-danger-surface',
  warning: 'text-warning hover:bg-warning-surface',
  success: 'text-success hover:bg-success-surface',
};

const SIZES: Record<IconButtonSize, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
};

/** With visible text the control grows sideways, so the square box becomes the
 *  same height on the token geometry the rest of the controls use. */
const HEIGHTS: Record<IconButtonSize, string> = {
  sm: 'h-[var(--control-h-sm)]',
  md: 'h-[var(--control-h-md)]',
  lg: 'h-[var(--control-h-lg)]',
};

const GLYPH_SIZES: Record<IconButtonSize, string> = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
};

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  /**
   * Required. An icon-only control has no visible text, so this is the only
   * name a screen reader can announce. A tooltip never replaces it.
   */
  label: string;
  icon: React.ReactNode;
  /**
   * Optional visible caption beside the glyph.
   *
   * A control whose whole meaning lives in `aria-label`/`title` is a hover-only
   * affordance: on a touch device there is no tooltip and no label to read. When
   * the caller can afford the width, passing the state as text makes the control
   * self-explanatory without changing its accessible name — `label` still names
   * it, and the caption is `aria-hidden` so it is never read twice.
   */
  text?: string;
  variant?: IconButtonVariant;
  tone?: IconButtonTone;
  size?: IconButtonSize;
  loading?: boolean;
}

export function IconButton({
  label,
  icon,
  text,
  variant = 'ghost',
  tone = 'default',
  size = 'md',
  loading = false,
  disabled,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'dhd-target relative inline-flex shrink-0 items-center justify-center rounded-xs',
        'transition-[transform,background-color,color,border-color] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
        'hover:-translate-y-px active:translate-y-px',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        TONES[tone],
        text ? cn(HEIGHTS[size], 'w-auto gap-2 px-3') : SIZES[size],
        className,
      )}
    >
      {loading ? (
        <Spinner size={size === 'sm' ? 'sm' : 'md'} />
      ) : (
        <span
          className={cn('grid place-items-center [&>svg]:size-full', GLYPH_SIZES[size])}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {text && !loading ? (
        <span
          aria-hidden="true"
          className="font-display text-2xs font-semibold uppercase tracking-label"
        >
          {text}
        </span>
      ) : null}
    </button>
  );
}
